import { DEFAULT_GOOGLE_CONSENT, DEFAULT_PREFERENCES, isUS, resolveEffectiveConsent, type GoogleConsentState, type IubendaPreferences, type ResolveInput } from "./model";
export const INITIAL_CONSENT_SNAPSHOT = {
  schemaVersion: 1,
  revision: 0,
  countryCode: "",
  ready: false,
  usAcknowledged: false,
  preferencesPending: false,
  preferencesOpen: false,
  noticeVisible: false,
  preferences: DEFAULT_PREFERENCES,
  google: DEFAULT_GOOGLE_CONSENT
};
export type ConsentSnapshot = typeof INITIAL_CONSENT_SNAPSHOT;
export type ConsentCallback = "ready" | "notNeeded" | "restored" | "saved" | "shown" | "closed";
type Environment = {
  read: () => ResolveInput;
  acknowledged: boolean;
  pending: boolean;
  persistAcknowledgment: () => void;
  persistPending: (pending: boolean) => void;
  updateGoogle: (state: GoogleConsentState) => void;
  observe: (snapshot: ConsentSnapshot) => void;
};
function copyPreferences(preferences?: IubendaPreferences): IubendaPreferences | undefined {
  if (!preferences) return undefined;
  return {
    ...(preferences.purposes && {
      purposes: {
        ...preferences.purposes
      }
    }),
    ...(preferences.uspr && {
      uspr: {
        ...preferences.uspr
      }
    })
  };
}

/** The only committed consent store. Vendor working preferences are never published while editing. */
export function createConsentStore(environment: Environment) {
  let snapshot = INITIAL_CONSENT_SNAPSHOT;
  let ready = false;
  let notNeeded = false;
  let committed: IubendaPreferences | undefined;
  let committedRead = false;
  let awaitingSavedRead = false;
  let savedReadMayRecover = true;
  let acknowledgeAfterRead = false;
  let acknowledged = environment.acknowledged;
  let pending = environment.pending && !acknowledged;
  let open = false;
  let openedSession = false;
  let noticeExposed = false;
  let lastGoogle = DEFAULT_GOOGLE_CONSENT;
  const listeners = new Set<() => void>();
  const publish = () => {
    const input = environment.read();
    const countryCode = input.countryCode ?? "";
    if (ready && !committedRead && !input.readFailed && !open && !openedSession) {
      committed = copyPreferences(input.preferences);
      committedRead = true;
    }
    if (awaitingSavedRead && savedReadMayRecover && !open && !input.readFailed) {
      committed = copyPreferences(input.preferences);
      committedRead = true;
      awaitingSavedRead = false;
      if (acknowledgeAfterRead && isUS(countryCode)) {
        environment.persistAcknowledgment();
        acknowledged = true;
        pending = false;
        environment.persistPending(false);
        open = false;
        openedSession = false;
      }
      acknowledgeAfterRead = false;
      openedSession = false;
    }
    const effective = resolveEffectiveConsent({
      ...input,
      readFailed: input.readFailed || awaitingSavedRead || !committedRead,
      preferences: committed,
      notNeeded,
      ready,
      usAcknowledged: acknowledged
    });
    const next: ConsentSnapshot = {
      schemaVersion: 1,
      revision: snapshot.revision,
      countryCode,
      ready,
      usAcknowledged: acknowledged,
      preferencesPending: pending,
      preferencesOpen: open,
      noticeVisible: ready && isUS(countryCode) && !acknowledged && !open,
      ...effective
    };
    if (JSON.stringify(next) === JSON.stringify(snapshot)) return;
    next.revision += 1;
    snapshot = next;

    // Queue Google's update before publishing the observational event or releasing app loaders.
    // Unchanged denied defaults do not prematurely end the head's finite wait_for_update.
    if (JSON.stringify(next.google) !== JSON.stringify(lastGoogle)) {
      environment.updateGoogle(next.google);
      lastGoogle = next.google;
    }
    environment.observe(next);
    listeners.forEach(listener => listener());
  };
  const setPending = (value: boolean) => {
    pending = value;
    environment.persistPending(value);
  };
  const acknowledge = (reason: "close" | "browsing") => {
    if (!snapshot.noticeVisible || !noticeExposed || reason === "browsing" && pending) return;
    environment.persistAcknowledgment();
    acknowledged = true;
    setPending(false);
    publish();
  };
  return {
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    exposeNotice() {
      noticeExposed = snapshot.noticeVisible;
    },
    acknowledge,
    beginPreferences() {
      // Recover a prior Save before a new editor can expose another working copy.
      publish();
      if (awaitingSavedRead) savedReadMayRecover = false;
      if (isUS(snapshot.countryCode) && !acknowledged) setPending(true);
      open = true;
      // on2ndLayerShown confirms opening. A failed open cannot become a saved acknowledgment.
      openedSession = false;
      publish();
    },
    receive(event: ConsentCallback) {
      if (event === "shown") {
        if (awaitingSavedRead) savedReadMayRecover = false;
        openedSession = true;
        open = true;
        if (isUS(snapshot.countryCode) && !acknowledged) setPending(true);
      } else if (event === "closed") {
        open = false;
        // Save can close the center before its asynchronous FirstExpressed callback.
        // Retain the session until Save or an explicit notice X; never grant on close.
      } else {
        const input = environment.read();
        ready = true;
        if (event === "notNeeded") notNeeded = true;
        if (event === "saved" && input.readFailed) {
          awaitingSavedRead = true;
          savedReadMayRecover = true;
          acknowledgeAfterRead = openedSession && isUS(input.countryCode);
        }
        if (!input.readFailed && (event === "saved" || !open && !openedSession && !awaitingSavedRead && (!committedRead || !pending))) {
          committed = copyPreferences(input.preferences);
          committedRead = true;
        }
        if (event === "saved" && !input.readFailed) {
          awaitingSavedRead = false;
          savedReadMayRecover = true;
          acknowledgeAfterRead = false;
        }
        if (!input.readFailed && event === "saved" && openedSession && isUS(input.countryCode)) {
          environment.persistAcknowledgment();
          acknowledged = true;
          setPending(false);
          open = false;
          openedSession = false;
        }
        if (event === "saved" && !input.readFailed) {
          open = false;
          openedSession = false;
        }
      }
      publish();
    },
    refresh() {
      const input = environment.read();
      if (!input.readFailed && !open && !pending && !openedSession && !awaitingSavedRead) committed = copyPreferences(input.preferences);
      publish();
    }
  };
}